import cx from 'classnames';
import { BarChart3, ImageIcon, Lock, Sigma, Workflow } from 'lucide-react';
import { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { absoluteUrl } from '../../../../utils/sseFetch';
import styles from './PreviewMarkdown.module.scss';

/**
 * 预览 / 只读正文渲染：
 * - 引用 `[[citation:n]]` → 上标 [n]
 * - ```mermaid / ```echart / ```latex / ```code 围栏 → 「图表将在解锁后生成」占位卡片
 * - ```markdown 围栏（表格）与普通 | 表格 → remark-gfm 直接渲染
 */

export const CITATION_RE = /\[\[citation:\s*([^\]]+?)\s*\]\]/g;
const MARKER_HEAD = '[[citation:';
const CITE_HREF = '#cite';
const CARET_HREF = '#caret';
const ZERO_WIDTH = String.fromCharCode(0x200b);

/** 流式打字时隐藏结尾尚未闭合的引用标记，避免闪现半截 `[[citation:1` */
export const stripPartialCitation = (text) => {
    if (typeof text !== 'string' || !text) return text || '';
    const lastOpen = text.lastIndexOf('[[');
    if (lastOpen !== -1 && text.indexOf(']]', lastOpen) === -1) {
        const tail = text.slice(lastOpen);
        const partial =
            tail.length <= MARKER_HEAD.length
                ? MARKER_HEAD.startsWith(tail)
                : tail.startsWith(MARKER_HEAD);
        if (partial) return text.slice(0, lastOpen);
    }
    return text.endsWith('[') ? text.slice(0, -1) : text;
};

/** 引用标记转成指向 #cite 的链接，渲染时换成上标（不依赖原始 HTML，正文里的 < 不会被当成标签） */
const citationsToLinks = (text) =>
    text.replace(CITATION_RE, (_, raw) => {
        const display = String(raw)
            .split(',')
            .map((part) => part.split('@')[0].trim())
            .filter(Boolean)
            .join(',');
        return `[[${display}]](${CITE_HREF})`;
    });

/** 打字光标：作为一个指向 #caret 的空链接追加在正文末尾；还在围栏里时不加（占位卡片本身就在） */
const withCaret = (text) => {
    const fences = (text.match(/^\s*(?:```|~~~)/gm) || []).length;
    if (fences % 2 === 1) return text;
    return `${text}[${ZERO_WIDTH}](${CARET_HREF})`;
};

const PLACEHOLDER_LANGS = {
    mermaid: { label: '示意图', Icon: Workflow },
    echart: { label: '数据图表', Icon: BarChart3 },
    echarts: { label: '数据图表', Icon: BarChart3 },
    latex: { label: '公式', Icon: Sigma },
    code: { label: '配图', Icon: ImageIcon },
};

const hastText = (node) => {
    if (!node) return '';
    if (node.type === 'text') return node.value || '';
    return (node.children || []).map(hastText).join('');
};

const codeLanguage = (codeNode) => {
    const raw = codeNode?.properties?.className;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const cls = list.map(String).find((c) => c.startsWith('language-'));
    return cls ? cls.slice(9).toLowerCase() : '';
};

/** 围栏第一行是图名 / 标题时作为占位卡片的图注 */
export const fenceCaption = (lang, text) => {
    const lines = String(text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    const first = lines[0] || '';
    let match = first.match(/^(?:图名|表名|图题|图片名称|标题|名称|title)\s*[:：]\s*(.+)$/i);
    if (match) return match[1].trim();
    match = first.match(/^%%\s*((?:图|表).+)$/);
    if (match) return match[1].trim();
    if (/^(?:图|表)\s*[\d一二三四五六七八九十.-]*\s*[^\s{};]/.test(first) && first.length <= 60) {
        if (!/^(?:图片类型|图片内容|图表类型)/.test(first)) return first;
    }
    if (lang === 'mermaid') {
        const titleLine = lines.find((line) => /^title\s+\S/i.test(line));
        if (titleLine) return titleLine.replace(/^title\s+/i, '').trim();
    }
    if (lang === 'echart' || lang === 'echarts') {
        const titleMatch = String(text || '').match(/"text"\s*:\s*"([^"]{1,60})"/);
        if (titleMatch) return titleMatch[1];
    }
    return '';
};

const fenceTypeHint = (text) => {
    const line = String(text || '')
        .split('\n')
        .map((l) => l.trim())
        .find((l) => /^(?:图片类型|图表类型|图类型)\s*[:：]/.test(l));
    return line ? line.replace(/^(?:图片类型|图表类型|图类型)\s*[:：]\s*/, '').slice(0, 20) : '';
};

function FigurePlaceholder({ lang, text, hint }) {
    const meta = PLACEHOLDER_LANGS[lang] || PLACEHOLDER_LANGS.code;
    const { Icon } = meta;
    const caption = fenceCaption(lang, text);
    const typeHint = fenceTypeHint(text);
    return (
        <figure className={styles.figure}>
            <span className={styles.figureIcon}>
                <Icon size={20} strokeWidth={1.8} />
            </span>
            <span className={styles.figureBody}>
                <span className={styles.figureTag}>
                    {meta.label}
                    {typeHint ? ` · ${typeHint}` : ''}
                </span>
                {caption ? <span className={styles.figureCaption}>{caption}</span> : null}
                <span className={styles.figureHint}>
                    <Lock size={12} />
                    {hint}
                </span>
            </span>
        </figure>
    );
}

const CAPTION_RE = /^(?:图|表)\s*[\d一二三四五六七八九十.-]*\s+\S.{0,40}$/;

const buildComponents = ({ placeholderText }) => {
    const components = {
        a({ href, children, node, ...rest }) {
            if (href === CITE_HREF) return <sup className={styles.cite}>{children}</sup>;
            if (href === CARET_HREF) return <span className={styles.caret} aria-hidden="true" />;
            return (
                <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
                    {children}
                </a>
            );
        },
        sup({ children, node, ...rest }) {
            return (
                <sup className={styles.cite} {...rest}>
                    {children}
                </sup>
            );
        },
        p({ children, node }) {
            const text = hastText(node).trim();
            const isCaption = text.length > 0 && text.length <= 48 && CAPTION_RE.test(text);
            return <p className={isCaption ? styles.caption : undefined}>{children}</p>;
        },
        img({ src, alt, node, ...rest }) {
            const url = src && /^\/?ai\//.test(src) ? absoluteUrl(src) : src;
            return <img src={url} alt={alt || ''} loading="lazy" {...rest} />;
        },
        table({ children, node }) {
            return (
                <div className={styles.tableWrap}>
                    <table>{children}</table>
                </div>
            );
        },
        pre({ children, node }) {
            const codeNode = (node?.children || []).find((c) => c.tagName === 'code');
            const lang = codeLanguage(codeNode);
            const text = hastText(codeNode);
            if (lang === 'markdown' || lang === 'md') {
                return (
                    <div className={styles.fenceMarkdown}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                            {citationsToLinks(text)}
                        </ReactMarkdown>
                    </div>
                );
            }
            if (PLACEHOLDER_LANGS[lang]) {
                return <FigurePlaceholder lang={lang} text={text} hint={placeholderText} />;
            }
            return <pre className={styles.pre}>{children}</pre>;
        },
    };
    return components;
};

const PREVIEW_COMPONENTS = buildComponents({ placeholderText: '图表将在解锁后生成' });

/**
 * @param {string} content 正文 Markdown
 * @param {boolean} typing 正在流式输出：隐藏结尾半截引用，末尾显示光标
 * @param {boolean} rawHtml 允许正文里的 HTML（只读文档里的 <sup> 等）
 * @param {'zh'|'en'} lang 英文摘要用 Times New Roman
 * @param {string} placeholderText 图表占位提示
 */
function PreviewMarkdown({
    content,
    typing = false,
    rawHtml = false,
    lang = 'zh',
    placeholderText,
    className,
}) {
    const text = citationsToLinks(typing ? stripPartialCitation(content) : content || '');
    const source = typing ? withCaret(text) : text;
    const components = useMemo(
        () => (placeholderText ? buildComponents({ placeholderText }) : PREVIEW_COMPONENTS),
        [placeholderText],
    );
    return (
        <div className={cx(styles.markdown, lang === 'en' && styles.en, className)}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={rawHtml ? [rehypeRaw] : []}
                components={components}
            >
                {source}
            </ReactMarkdown>
        </div>
    );
}

export default memo(PreviewMarkdown);
